import { Component, DestroyRef, ElementRef, computed, effect, inject, input, output, signal, viewChild } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TelemetryService, ToasterService, WORKFLOW_ENGINE_GATEWAY, WorkflowService } from '#services';
import { TelemetryEvent } from '#enums';
import {
  formatTimestamp,
  WAT_TIME_ZONE,
  WorkflowBadgeStatus,
  WorkflowComment,
  WorkflowItem,
  WorkflowStep,
} from '#models';
import { normalizeReportBlocks } from '../mappers/report-block.mapper';
import { exceptionTypeLabel } from '../../exception-type-label';
import type { FeedbackType } from '../models/detail-view-model';
import { DetailHeaderComponent } from '../components/detail-header/detail-header.component';
import { DetailStepsComponent } from '../components/detail-steps/detail-steps.component';
import { DetailCommentModalComponent } from '../components/detail-comment-modal/detail-comment-modal.component';
import { DetailCommentsComponent } from '../components/detail-comments/detail-comments.component';
import { DetailRejectModalComponent } from '../components/detail-reject-modal/detail-reject-modal.component';
import { DetailFeedbackModalComponent } from '../components/detail-feedback-modal/detail-feedback-modal.component';
import { EngineCaseDetailComponent } from '../engine/engine-case-detail/engine-case-detail.component';
import { WellExceptionLiveWorkflowComponent } from '../engine/well-exception-live-workflow/well-exception-live-workflow.component';
import { ProductionDeviationWorkflowComponent } from '../components/production-deviation-workflow/production-deviation-workflow.component';
import { isProductionDeviationItem } from '../mappers/production-deviation-workflow.mapper';
import { MeRolesResponse } from '../../../../services/workflow-engine/generated/models/me-roles-response';
import { WorkflowLoadFeedbackService } from '../../workflow-load-feedback.service';
import { PersistedCaseDetailComponent } from '../components/persisted-case-detail/persisted-case-detail.component';

@Component({
  selector: 'app-workflow-detail',
  imports: [
    DetailHeaderComponent,
    DetailStepsComponent,
    DetailCommentModalComponent,
    DetailCommentsComponent,
    DetailRejectModalComponent,
    DetailFeedbackModalComponent,
    EngineCaseDetailComponent,
    WellExceptionLiveWorkflowComponent,
    ProductionDeviationWorkflowComponent,
    NgTemplateOutlet,
    PersistedCaseDetailComponent,
  ],
  templateUrl: './workflow-detail-shell.component.html',
  styleUrl: './workflow-detail-shell.component.scss',
})
export class WorkflowDetailShellComponent {
  private _workflowService = inject(WorkflowService);
  private _workflowEngineGateway = inject(WORKFLOW_ENGINE_GATEWAY);
  private _toasterService = inject(ToasterService);
  private _telemetry = inject(TelemetryService);
  private _destroyRef = inject(DestroyRef);
  private _loadFeedback = inject(WorkflowLoadFeedbackService, { optional: true });
  loadingSettled = computed(() => this._loadFeedback?.settled() ?? true);

  positiveFeedbackButton = viewChild<ElementRef<HTMLButtonElement>>('positiveFeedbackButton');
  negativeFeedbackButton = viewChild<ElementRef<HTMLButtonElement>>('negativeFeedbackButton');

  item = input<WorkflowItem | null>(null);
  requestedTab = input<string | null>(null);
  tabSelected = output<string>();

  approve = output<void>();
  reject = output<string>();


  private _feedbackModalOpen = signal<boolean>(false);
  private _myRoles = signal<MeRolesResponse | null>(null);
  private _feedbackType = signal<FeedbackType>('up');
  private _selectedFeedbackReasons = signal<Set<string>>(new Set());
  private _feedbackComment = signal<string>('');
  private _submittingFeedback = signal<boolean>(false);
  private _feedbackError = signal<boolean>(false);

  feedbackModalOpen = this._feedbackModalOpen.asReadonly();
  myRoles = this._myRoles.asReadonly();
  feedbackType = this._feedbackType.asReadonly();
  selectedFeedbackReasons = this._selectedFeedbackReasons.asReadonly();
  feedbackComment = this._feedbackComment.asReadonly();
  submittingFeedback = this._submittingFeedback.asReadonly();
  feedbackError = this._feedbackError.asReadonly();

  private readonly positiveFeedbackReasons = [
    'The results were accurate.',
    'The results were relevant to me and helped me make decisions faster.',
    'The results were complete and I did not need to look elsewhere for missing information.',
  ];
  private readonly negativeFeedbackReasons = [
    'The results were inaccurate and/or contained mistakes.',
    'The results were not relevant to me and I had to search longer than I needed to.',
    'The results were lacking in information and I had to search elsewhere to find what I needed.',
  ];

  feedbackReasons = computed<string[]>(() =>
    this._feedbackType() === 'up' ? this.positiveFeedbackReasons : this.negativeFeedbackReasons,
  );

  private _comments = signal<WorkflowComment[]>([]);
  private _commentsOpen = signal<boolean>(false);
  private _commentModalOpen = signal<boolean>(false);
  private _commentDraft = signal<string>('');
  private _savingComment = signal<boolean>(false);
  private _commentError = signal<boolean>(false);
  // Comments posted in this session; kept across same-case refreshes until the server snapshot includes them.
  private _savedComments: WorkflowComment[] = [];

  comments = this._comments.asReadonly();
  commentsOpen = this._commentsOpen.asReadonly();
  commentModalOpen = this._commentModalOpen.asReadonly();
  commentDraft = this._commentDraft.asReadonly();
  savingComment = this._savingComment.asReadonly();
  commentError = this._commentError.asReadonly();

  private _rejectModalOpen = signal<boolean>(false);
  private _rejectDraft = signal<string>('');

  rejectModalOpen = this._rejectModalOpen.asReadonly();
  rejectDraft = this._rejectDraft.asReadonly();

  isClosed = computed<boolean>(() => this.item()?.badge === WorkflowBadgeStatus.Closed);
  isProductionDeviation = computed<boolean>(() => isProductionDeviationItem(this.item()));
  /** The engine case a legacy record's Dossier opened. */
  linkedEngineCaseId = computed<string | null>(() => this.item()?.linkedEngineCaseId ?? null);
  /** A linked UC1 case shows in the production-deviation workflow view; any other linked case in the generic engine view. */
  linkedProductionDeviationCaseId = computed<string | null>(() => (this.isProductionDeviation() ? this.linkedEngineCaseId() : null));
  private _linkedEngineWorkflow = viewChild(ProductionDeviationWorkflowComponent);
  private _linkedEngineDetail = viewChild(EngineCaseDetailComponent);
  private _linkedUc2Workflow = viewChild(WellExceptionLiveWorkflowComponent);
  workflowContentReady = computed(() => {
    const store = this._linkedEngineWorkflow()?.store ?? this._linkedUc2Workflow()?.store ?? this._linkedEngineDetail()?.store;
    return this.loadingSettled() && !store?.loading();
  });
  hasUc2Workflow = computed(() => !!this.linkedEngineCaseId() && !this._linkedUc2Workflow()?.store.caseMissing());
  // The workflow view loads its engine case while its tab is hidden; its store holds the case only after every read it
  // renders from (state, graph, audit, Dossier, roles, active-step form) has succeeded, and only for the case it was given.
  private _linkedEngineReady = computed<boolean>(() => {
    const caseId = this.linkedEngineCaseId();
    const store = this._linkedEngineWorkflow()?.store ?? this._linkedUc2Workflow()?.store ?? this._linkedEngineDetail()?.store;
    return !!caseId && store?.composed()?.state.caseId === caseId;
  });
  hasEngineWorkflow = computed<boolean>(() =>
    !!this.linkedProductionDeviationCaseId() || this._linkedEngineReady(),
  );
  /** The feature badge follows the linked tab, not a successful engine read. */
  workflowTabBadge = computed<string | null>(() =>
    this._linkedEngineReady() || (this.hasUc2Workflow() && !!this._linkedUc2Workflow()) ? 'New' : null,
  );

  getCreatedLabel(): string {
    return this.formatBannerDate(this.item()?.createdDate);
  }

  getUpdatedLabel(): string {
    return this.formatBannerDate(this.item()?.updatedDate);
  }

  constructor() {
    let lastViewedId: string | null = null;
    effect(() => {
      const currentItem = this.item();
      if (currentItem && currentItem.id !== lastViewedId) {
        this._telemetry.trackAppEvent(TelemetryEvent.CaseViewed, {
          workflowId: currentItem.id,
          engineCaseId: currentItem.engineCaseId ?? currentItem.linkedEngineCaseId,
        });
      }
      lastViewedId = currentItem?.id ?? null;
    });

    effect(() => {
      this.item();
      this._feedbackModalOpen.set(false);
      this._feedbackComment.set('');
      this._selectedFeedbackReasons.set(new Set());
      this._submittingFeedback.set(false);
      this._feedbackError.set(false);
      this._rejectModalOpen.set(false);
      this._rejectDraft.set('');
    });

    let commentsCaseId: string | null = null;
    effect(() => {
      const currentItem = this.item();
      const incoming = currentItem?.comments ?? [];
      if (currentItem?.id !== commentsCaseId) {
        commentsCaseId = currentItem?.id ?? null;
        this._savedComments = [];
        this._comments.set(incoming);
        this._commentsOpen.set(false);
        this._commentModalOpen.set(false);
        this._commentDraft.set('');
        this._commentError.set(false);
        return;
      }

      // Same case refreshed (e.g. detail loaded): keep the draft and any posted comment the snapshot lacks.
      const incomingIds = new Set(incoming.map(comment => comment.id));
      this._savedComments = this._savedComments.filter(comment => !incomingIds.has(comment.id));
      this._comments.set([...this._savedComments, ...incoming]);
    });

    effect(() => {
      const currentItem = this.item();
      if (!currentItem || currentItem.persistedCase) {
        this._myRoles.set(null);
        return;
      }

      this._workflowEngineGateway.getMyRoles()
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          next: roles => this._myRoles.set(roles),
          error: error => {
            console.error('Failed to load workflow engine roles.', { status: error.status });
            this._myRoles.set(null);
          },
        });
    });
  }

  decidedStep = computed<WorkflowStep | null>(() => {
    const steps = this.item()?.steps ?? [];
    for (let index = steps.length - 1; index >= 0; index--) {
      if (steps[index].action) {
        return steps[index];
      }
    }
    return null;
  });

  summaryBlocks = computed(() => normalizeReportBlocks(this.item()?.summary));
  summaryText = computed(() => this.item()?.summary ?? this.item()?.identity?.confidenceStatement ?? '');

  recommendedActions = computed<string[]>(() => {
    const actionText = this.item()?.actionText;
    if (!actionText) {
      return [];
    }

    return actionText
      .split(';')
      .map(task => task.trim())
      .filter(task => task.length > 0);
  });

  recommendedActionSources = computed<string[]>(() => {
    const current = this.item();
    if (!current) {
      return [];
    }

    const stream = current.identity?.stream || current.asset || current.id;
    const type = exceptionTypeLabel(current);
    return [
      `${stream} · Fabric ontology analysis`,
      `Allocation model — ${type}`,
    ];
  });

  previewAssetCodes = computed<string[]>(() => this.parseAssetCodes(this.item()?.asset));

  private formatBannerDate(value: string | null | undefined): string {
    if (!value) {
      return '--';
    }

    const parsed = Date.parse(value);
    if (Number.isNaN(parsed)) {
      return value;
    }

    if (this.item()?.persistedCase) {
      return new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Africa/Lagos', day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: false,
      }).format(parsed) + ' WAT';
    }

    return formatTimestamp(parsed, { seconds: true, timeZone: WAT_TIME_ZONE });
  }

  onReject(): void {
    this._rejectDraft.set('');
    this._rejectModalOpen.set(true);
  }

  closeRejectModal(): void {
    this._rejectModalOpen.set(false);
  }

  onRejectInput(value: string): void {
    this._rejectDraft.set(value);
  }

  confirmReject(): void {
    const feedback = this._rejectDraft().trim();
    if (!feedback) {
      return;
    }

    this._rejectModalOpen.set(false);
    this.reject.emit(feedback);
  }

  onFeedback(thumb: FeedbackType): void {
    if (!this.item()?.id) {
      return;
    }

    this._feedbackType.set(thumb);
    this._selectedFeedbackReasons.set(new Set());
    this._feedbackComment.set('');
    this._feedbackError.set(false);
    this._feedbackModalOpen.set(true);
  }

  closeFeedbackModal(): void {
    if (this._submittingFeedback()) {
      return;
    }

    this._closeFeedbackModalAndRestoreFocus();
  }

  toggleFeedbackReason(reason: string): void {
    this._selectedFeedbackReasons.update(selected => {
      const next = new Set(selected);
      if (next.has(reason)) {
        next.delete(reason);
      } else {
        next.add(reason);
      }
      return next;
    });
  }

  onFeedbackCommentInput(value: string): void {
    this._feedbackComment.set(value);
  }

  submitFeedback(): void {
    const id = this.item()?.id;
    const thumb = this._feedbackType();
    if (!id || this._submittingFeedback()) {
      return;
    }

    this._submittingFeedback.set(true);
    this._feedbackError.set(false);
    const reasons = [...this._selectedFeedbackReasons()];
    this._workflowService
      .submitFeedback(id, thumb, reasons, this._feedbackComment())
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          this._telemetry.trackAppEvent(TelemetryEvent.CaseFeedbackSubmitted, { workflowId: id, thumb, reasonCount: reasons.length });
          this._submittingFeedback.set(false);
          this._showFeedbackConfirmation();
        },
        error: error => {
          this._telemetry.trackCommandFailed('submit_feedback', error, { workflowId: id });
          this._submittingFeedback.set(false);
          this._feedbackError.set(true);
        },
      });
  }

  private _restoreFeedbackTriggerFocus(): void {
    const trigger = this._feedbackType() === 'up'
      ? this.positiveFeedbackButton()
      : this.negativeFeedbackButton();
    queueMicrotask(() => trigger?.nativeElement.focus());
  }

  private _closeFeedbackModalAndRestoreFocus(): void {
    this._feedbackModalOpen.set(false);
    this._restoreFeedbackTriggerFocus();
  }

  private _showFeedbackConfirmation(): void {
    this._feedbackModalOpen.set(false);
    this._toasterService.showSuccess('Thank you for your feedback!');
    this._restoreFeedbackTriggerFocus();
  }

  toggleComments(): void {
    this._commentsOpen.update(open => !open);
  }

  openCommentModal(): void {
    this._commentDraft.set('');
    this._commentError.set(false);
    this._commentModalOpen.set(true);
  }

  closeCommentModal(): void {
    if (this._savingComment()) {
      return;
    }

    this._commentModalOpen.set(false);
  }

  onCommentInput(value: string): void {
    this._commentDraft.set(value);
  }

  saveComment(): void {
    const id = this.item()?.id;
    const text = this._commentDraft().trim();
    if (!id || !text || this._savingComment()) {
      return;
    }

    this._savingComment.set(true);
    this._commentError.set(false);
    this._workflowService
      .addComment(id, text)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: comment => {
          this._telemetry.trackAppEvent(TelemetryEvent.CaseCommentAdded, { workflowId: id });
          this._savingComment.set(false);
          if (this.item()?.id !== id) {
            return; // The user moved to another case while this was saving.
          }
          this._savedComments = [comment, ...this._savedComments];
          this._comments.update(list => [comment, ...list]);
          this._commentModalOpen.set(false);
          this._commentDraft.set('');
          this._commentsOpen.set(true);
        },
        error: error => {
          this._telemetry.trackCommandFailed('add_comment', error, { workflowId: id });
          this._savingComment.set(false);
          this._commentError.set(true);
        },
      });
  }

  private parseAssetCodes(rawAsset: string | undefined): string[] {
    const value = String(rawAsset ?? '').trim();
    if (!value) {
      return [];
    }

    return Array.from(
      new Set(
        value
          .split(/[;,|\n\r]+/)
          .map(part => part.trim())
          .filter(part => part.length > 0)
          .map(part => part.toLowerCase())
      )
    );
  }
}
